import { useCallback, useRef, useState } from 'react';
import {
  runScreenRefresh,
  type RunScreenRefreshOptions,
  type ScreenRefreshResult,
} from '../screenRefreshFeedback';

type ReloadFn = () => Promise<ScreenRefreshResult | void>;

/**
 * Refresh unificado para header web y pull-to-refresh en app:
 * - guard de doble tap
 * - estado `refreshing`
 * - toast estándar (éxito / sin conexión / aviso parcial / error)
 */
export function useScreenRefresh(
  reload: ReloadFn,
  options?: Pick<RunScreenRefreshOptions, 'feedback'>
) {
  const [refreshing, setRefreshing] = useState(false);
  const inFlightRef = useRef(false);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  const feedback = options?.feedback ?? true;

  const runRefresh = useCallback(
    async (
      runOptions?: Pick<RunScreenRefreshOptions, 'feedback'> & { reload?: ReloadFn }
    ) => {
      const reloadFn = runOptions?.reload ?? (() => reloadRef.current());
      await runScreenRefresh(reloadFn, {
        feedback: runOptions?.feedback ?? feedback,
        setRefreshing,
        inFlightRef,
      });
    },
    [feedback]
  );

  const onRefresh = useCallback(async () => {
    await runRefresh();
  }, [runRefresh]);

  return {
    refreshing,
    onRefresh,
    runRefresh,
    /** Alias usado en algunos view-models legacy. */
    isRefreshing: refreshing,
  };
}
